/** @file images.rescale: the puppet's pixels times 2 or 4 in one step (images, pivots, tilePx; CR-011). */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import type { Drawable } from '@zilo/pose3d-format/types/drawable';
import type { FacetEntry } from '@zilo/pose3d-format/types/facet';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import { checkRecord } from '@zilo/pose3d-format/validate/fields/checkRecord';
import { checkString } from '@zilo/pose3d-format/validate/fields/checkString';
import { failField } from '@zilo/pose3d-format/validate/fields/failField';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import type { CommandDef } from '../apply/commandDef';
import { puppetInverse } from '../apply/puppetInverse';
import type { CommandContext } from '../context/commandContext';
import { checkPuppet } from '../puppetDoc/checkPuppet';
import type { PuppetDoc } from '../puppetDoc/puppetDoc';
import { compact } from '../validate/compact';
import { pointerToken } from '../validate/pointerToken';
import { readInput } from '../validate/readInput';
import { requireProfile } from '../validate/requireProfile';

const FACTORS = [2, 4] as const;
const imageMap = checkRecord(checkString(), /^.+$/);

const times = (v: [number, number], k: number): [number, number] => [v[0] * k, v[1] * k];

/** Every facet entry that names images: impostor entries and effect facets. */
const entriesOf = (p: Puppet): FacetEntry[] => p.drawables.flatMap((d) => (d.kind === 'impostor' || d.kind === 'effect' ? (d.facets ?? []) : []));

/** The images the puppet references (sheets, then entry slots), each once, sorted. */
function referenced(p: Puppet): string[] {
  const ids = new Set(Object.values(p.sheets).map((s) => s.image));
  for (const e of entriesOf(p)) for (const image of Object.values(e.slots)) ids.add(image);
  return [...ids].sort();
}

/** `images[id]` for every referenced image, sized `factor` times the old one (VALIDATION / NOT_FOUND otherwise). */
function checkMap(draft: PuppetDoc, ids: string[], input: { factor: number; images?: Record<string, string> }, ctx: CommandContext): Record<string, string> {
  const map = input.images ?? {};
  for (const id of ids) {
    const to = map[id];
    const path = `/input/images/${pointerToken(id)}`;
    if (to === undefined) {
      throwError(ERROR_CODES.VALIDATION, { image: id, expected: 'a scaled copy' }, {
        path, hint: 'send images.rescale alone through the batch (REST or MCP): the server scales and stores every image and fills images',
      });
    }
    const from = draft.images[id] ?? ctx.images[id] ?? throwError(ERROR_CODES.NOT_FOUND, { image: id }, { path: `/images/${id}`, hint: 'upload the image first' });
    const meta = ctx.images[to] ?? throwError(ERROR_CODES.NOT_FOUND, { image: to }, { path, hint: 'upload the scaled image first' });
    const want = times(from.size, input.factor);
    if (meta.size[0] !== want[0] || meta.size[1] !== want[1]) {
      throwError(ERROR_CODES.VALIDATION, { image: to, expected: want, actual: meta.size }, { path, hint: `the scaled copy of ${id} is ${want[0]} x ${want[1]}` });
    }
  }
  return map;
}

/** The new profile id, when given: known, with `factor` times the old `pixelsPerUnit` (VALIDATION at /input/profile). */
function checkProfile(p: Puppet, input: { factor: number; profile?: string }, ctx: CommandContext): string {
  if (input.profile === undefined) return p.profile;
  const old = ctx.profiles[requireProfile(p.profile, ctx, '/puppet/profile')]?.pixelsPerUnit ?? 0;
  const next = ctx.profiles[requireProfile(input.profile, ctx)]?.pixelsPerUnit ?? 0;
  if (next !== old * input.factor) {
    throwError(ERROR_CODES.VALIDATION, { profile: input.profile, expected: old * input.factor, actual: next }, {
      path: '/input/profile', hint: `a profile with pixelsPerUnit ${old * input.factor} (profile_from_tile with every tile number times ${input.factor}), or none`,
    });
  }
  return input.profile;
}

/** An effect's pivot and frame strip, or a two-point inset (`anchor.pixelOffset`, image px), times `k`. */
function rescaleDrawable(d: Drawable, k: number): void {
  if (d.kind === 'effect') Object.assign(d, { pivot: times(d.pivot, k), frames: { ...d.frames, origin: times(d.frames.origin, k), size: times(d.frames.size, k) } });
  if (d.kind === 'impostor' && d.anchor?.pixelOffset !== undefined) d.anchor.pixelOffset *= k;
}

/** Scales the pixel fields in place and points the images at their copies. */
function rescale(p: Puppet, k: number, map: Record<string, string>): void {
  const to = (id: string): string => map[id] ?? id;
  for (const s of Object.values(p.sheets)) Object.assign(s, { image: to(s.image), size: times(s.size, k) });
  for (const e of entriesOf(p)) {
    Object.assign(e, { origin: times(e.origin, k), size: times(e.size, k), slots: Object.fromEntries(Object.entries(e.slots).map(([t, id]) => [t, to(id)])) });
  }
  for (const d of p.drawables) rescaleDrawable(d, k);
  const tile = p.profileOverrides?.tilePx;
  if (p.profileOverrides !== undefined && tile !== undefined) p.profileOverrides.tilePx = [tile[0] * k, tile[1] * k, tile[2] * k];
}

/**
 * `images.rescale { factor: 2 | 4, sampling?: 'nearest', profile?, images? }` → `{ factor, images, profile }`: every
 * referenced image becomes its scaled copy (`images` maps old → new; the server fills it), sheet sizes, entry origins
 * and sizes, effect pivots and frames, `anchor.pixelOffset` and `profileOverrides.tilePx` are multiplied by `factor`
 * (`pivotFrac` is a fraction and stays). `profile` switches to a profile with `factor` times the pixels per unit.
 * Inverse P (the old images stay in the store).
 */
export const imagesRescaleCommand: CommandDef<'images.rescale'> = {
  name: 'images.rescale',
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => {
    const r = readInput(input, path, ['factor', 'sampling', 'profile', 'images']);
    const factor = r.raw('factor');
    if (!FACTORS.includes(factor as 2 | 4)) failField(r.at('factor'), 'one of 2, 4', factor, 'images.rescale doubles (2) or quadruples (4) every pixel size');
    return compact({
      factor: factor as 2 | 4, sampling: r.opt('sampling', (k) => r.oneOf(k, ['nearest'] as const)), profile: r.opt('profile', r.str),
      images: r.opt('images', (k) => ({ ...(r.check(k, imageMap) as Record<string, string>) })),
    });
  },
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input, ctx) => {
    const p = draft.puppet;
    const ids = referenced(p);
    const map = checkMap(draft, ids, input, ctx);
    const profile = checkProfile(p, input, ctx);
    const inverse = puppetInverse(draft);
    rescale(p, input.factor, map);
    p.profile = profile;
    checkPuppet(draft, ctx);
    return { result: { factor: input.factor, images: ids.length, profile }, warnings: [], inverse };
  },
};
